import * as jose from 'jose';

/** Supported CIAM environments with their base URLs */
export const CIAM_ENVIRONMENTS = {
  PROD: 'https://api.ciam.dnb.no',
  TEST: 'https://api.uat.ciam.tech-03.net',
} as const;

export type CiamEnvironment = keyof typeof CIAM_ENVIRONMENTS;

/** Supported audience path patterns for CIAM JWT tokens */
export const CIAM_AUDIENCE_PATHS = ['/clients/v1', '/clients/v2', '/ocm-api/v3/clients'] as const;

/** All valid CIAM audience URLs (environment + path combinations) */
export const VALID_CIAM_AUDIENCES = Object.values(CIAM_ENVIRONMENTS).flatMap((baseUrl) =>
  CIAM_AUDIENCE_PATHS.map((path) => `${baseUrl}${path}`),
);

/** Checks if an audience matches a valid CIAM audience URL */
export function isValidCiamAudience(audience: string): boolean {
  return VALID_CIAM_AUDIENCES.includes(audience);
}

/** Gets the environment (PROD/TEST) for a given audience URL, or null if invalid */
export function getCiamEnvironment(audience: string): CiamEnvironment | null {
  for (const [env, baseUrl] of Object.entries(CIAM_ENVIRONMENTS)) {
    if (CIAM_AUDIENCE_PATHS.some((path) => audience === `${baseUrl}${path}`)) {
      return env as CiamEnvironment;
    }
  }
  return null;
}

/** Allowed algorithms for CIAM JWT tokens */
export const ALLOWED_ALGORITHMS = {
  ES256: 'ECDSA using P-256 and SHA-256',
  RS256: 'RSA using SHA-256',
};

export type AllowedAlgorithm = keyof typeof ALLOWED_ALGORITHMS;

/** Maximum allowed token expiry duration in days */
export const MAX_TOKEN_EXPIRY_DAYS = 30;

/** Maximum allowed token expiry duration in seconds (30 days) */
export const MAX_TOKEN_EXPIRY_SECONDS = MAX_TOKEN_EXPIRY_DAYS * 24 * 60 * 60;

/** Error codes for JWT validation */
export enum JwtErrorCode {
  MALFORMED_TOKEN = 'MALFORMED_TOKEN',
  INVALID_PAYLOAD = 'INVALID_PAYLOAD',
  SIGNATURE_VERIFICATION_FAILED = 'SIGNATURE_VERIFICATION_FAILED',
  MISSING_CNF_CLAIM = 'MISSING_CNF_CLAIM',
  MISSING_CNF_JWK = 'MISSING_CNF_JWK',
  MISSING_CNF_JWK_KID = 'MISSING_CNF_JWK_KID',
  INVALID_CNF_JWK = 'INVALID_CNF_JWK',
  TOKEN_EXPIRED = 'TOKEN_EXPIRED',
  TOKEN_NOT_YET_VALID = 'TOKEN_NOT_YET_VALID',
  TOKEN_EXPIRY_TOO_LONG = 'TOKEN_EXPIRY_TOO_LONG',
  MISSING_EXP_CLAIM = 'MISSING_EXP_CLAIM',
  INVALID_EXP_CLAIM = 'INVALID_EXP_CLAIM',
  INVALID_NBF_CLAIM = 'INVALID_NBF_CLAIM',
  INVALID_IAT_CLAIM = 'INVALID_IAT_CLAIM',
  INVALID_AUDIENCE = 'INVALID_AUDIENCE',
  MISSING_AUDIENCE = 'MISSING_AUDIENCE',
  INVALID_ALGORITHM = 'INVALID_ALGORITHM',
  EMPTY_TOKEN = 'EMPTY_TOKEN',
}

/** Human-readable error messages for JWT validation errors */
export const JWT_ERROR_MESSAGES: Record<JwtErrorCode, string> = {
  [JwtErrorCode.MALFORMED_TOKEN]: 'Token is malformed. Expected format: header.payload.signature',
  [JwtErrorCode.INVALID_PAYLOAD]: 'Token payload is invalid or cannot be decoded',
  [JwtErrorCode.SIGNATURE_VERIFICATION_FAILED]: 'Signature verification failed',
  [JwtErrorCode.MISSING_CNF_CLAIM]:
    'Missing cnf (confirmation) claim. RFC 7800 requires the cnf claim for proof-of-possession',
  [JwtErrorCode.MISSING_CNF_JWK]:
    'Missing jwk in cnf claim. The cnf claim must contain a jwk (JSON Web Key) for signature verification',
  [JwtErrorCode.MISSING_CNF_JWK_KID]: 'Missing kid in cnf.jwk. The JWK must include a key ID (kid) field',
  [JwtErrorCode.INVALID_CNF_JWK]: 'Invalid JWK in cnf claim. The jwk must be a valid JSON Web Key',
  [JwtErrorCode.TOKEN_EXPIRED]: 'Token has expired (exp claim)',
  [JwtErrorCode.TOKEN_NOT_YET_VALID]: 'Token is not yet valid (nbf claim)',
  [JwtErrorCode.TOKEN_EXPIRY_TOO_LONG]: `Token expiry exceeds maximum allowed duration of ${MAX_TOKEN_EXPIRY_DAYS} days`,
  [JwtErrorCode.MISSING_EXP_CLAIM]: 'Missing exp claim. Token must have an expiration time',
  [JwtErrorCode.INVALID_EXP_CLAIM]: 'Invalid exp claim. Must be a numeric Unix timestamp',
  [JwtErrorCode.INVALID_NBF_CLAIM]: 'Invalid nbf claim. Must be a numeric Unix timestamp',
  [JwtErrorCode.INVALID_IAT_CLAIM]: 'Invalid iat claim. Must be a numeric Unix timestamp not in the future',
  [JwtErrorCode.INVALID_AUDIENCE]: `Invalid audience (aud claim). Supported environments: PROD (${CIAM_ENVIRONMENTS.PROD}), TEST (${CIAM_ENVIRONMENTS.TEST}). Supported paths: ${CIAM_AUDIENCE_PATHS.join(', ')}`,
  [JwtErrorCode.MISSING_AUDIENCE]: 'Missing audience (aud claim)',
  [JwtErrorCode.INVALID_ALGORITHM]: 'Invalid algorithm (alg). Only ES256 and RS256 are allowed',
  [JwtErrorCode.EMPTY_TOKEN]: 'Token cannot be empty',
};

/** RFC 7800 Confirmation (cnf) claim structure @see https://datatracker.ietf.org/doc/html/rfc7800 */
export interface CnfClaim {
  jwk?: jose.JWK;
}

export interface DecodedJwt {
  header: jose.JWTHeaderParameters;
  payload: jose.JWTPayload;
  signature: string;
}

/**
 * Validates a JWT token and returns an array of error codes.
 * Always verifies signature using RFC 7800 cnf.jwk claim.
 * @param token - The JWT token string to validate
 * @returns Array of error codes (empty if valid)
 */
export async function validateJwt(token: string): Promise<JwtErrorCode[]> {
  if (!token?.trim()) return [JwtErrorCode.EMPTY_TOKEN];

  let decoded: DecodedJwt;
  try {
    decoded = decodeJwt(token);
  } catch {
    return [JwtErrorCode.MALFORMED_TOKEN];
  }

  const errors: JwtErrorCode[] = [];

  // Validate algorithm
  if (!decoded.header.alg || !(decoded.header.alg in ALLOWED_ALGORITHMS)) {
    errors.push(JwtErrorCode.INVALID_ALGORITHM);
  }

  // Validate time-based claims
  validateTimeClaims(decoded.payload, errors);

  // Validate audience claim
  validateAudienceClaim(decoded.payload, errors);

  // Verify signature using RFC 7800 cnf.jwk
  await validateSignature(token, decoded, errors);

  return errors;
}

/** Validates time-based claims (exp, nbf, iat) */
function validateTimeClaims(payload: jose.JWTPayload, errors: JwtErrorCode[]): void {
  const now = Math.floor(Date.now() / 1000);
  const { iat, exp, nbf } = payload;

  // Validate iat (optional, but if present must not be in future)
  validateIat(iat, now, errors);

  // Validate exp (required, must not be expired, must be within 30 days from now)
  validateExp(exp, now, errors);

  // Validate nbf (optional)
  validateNbf(nbf, now, errors);
}

function validateIat(iat: number | undefined, now: number, errors: JwtErrorCode[]): void {
  if (iat === undefined) return;
  if (typeof iat !== 'number' || iat > now) {
    errors.push(JwtErrorCode.INVALID_IAT_CLAIM);
  }
}

function validateExp(exp: number | undefined, now: number, errors: JwtErrorCode[]): void {
  if (exp === undefined) {
    errors.push(JwtErrorCode.MISSING_EXP_CLAIM);
    return;
  }
  if (typeof exp !== 'number') {
    errors.push(JwtErrorCode.INVALID_EXP_CLAIM);
    return;
  }
  if (exp < now) errors.push(JwtErrorCode.TOKEN_EXPIRED);
  if (exp > now + MAX_TOKEN_EXPIRY_SECONDS) errors.push(JwtErrorCode.TOKEN_EXPIRY_TOO_LONG);
}

function validateNbf(nbf: number | undefined, now: number, errors: JwtErrorCode[]): void {
  if (nbf === undefined) return;
  if (typeof nbf !== 'number') {
    errors.push(JwtErrorCode.INVALID_NBF_CLAIM);
    return;
  }
  if (nbf > now) errors.push(JwtErrorCode.TOKEN_NOT_YET_VALID);
}

/** Validates audience (aud) claim against valid CIAM audiences */
function validateAudienceClaim(payload: jose.JWTPayload, errors: JwtErrorCode[]): void {
  const aud = payload.aud;
  if (!aud) {
    errors.push(JwtErrorCode.MISSING_AUDIENCE);
    return;
  }
  const audiences = Array.isArray(aud) ? aud : [aud];
  if (!audiences.some((a) => isValidCiamAudience(a))) {
    errors.push(JwtErrorCode.INVALID_AUDIENCE);
  }
}

/** Validates JWT signature using RFC 7800 cnf.jwk claim */
async function validateSignature(token: string, decoded: DecodedJwt, errors: JwtErrorCode[]): Promise<void> {
  const cnf = decoded.payload['cnf'] as CnfClaim | undefined;

  if (!cnf) {
    errors.push(JwtErrorCode.MISSING_CNF_CLAIM);
    return;
  }
  if (!cnf.jwk) {
    errors.push(JwtErrorCode.MISSING_CNF_JWK);
    return;
  }
  if (!cnf.jwk.kid) {
    errors.push(JwtErrorCode.MISSING_CNF_JWK_KID);
    return;
  }

  const algorithm = decoded.header.alg as AllowedAlgorithm;

  let publicKey: jose.KeyLike | Uint8Array;
  try {
    publicKey = await jose.importJWK(cnf.jwk, algorithm);
  } catch {
    errors.push(JwtErrorCode.INVALID_CNF_JWK);
    return;
  }

  try {
    await jose.jwtVerify(token, publicKey, { algorithms: [algorithm] });
  } catch {
    errors.push(JwtErrorCode.SIGNATURE_VERIFICATION_FAILED);
  }
}

/** Decodes a JWT token without verification */
export function decodeJwt(token: string): DecodedJwt {
  const parts = token.split('.');
  if (parts.length !== 3 || !parts[2]) throw new Error('Malformed token');

  const header = jose.decodeProtectedHeader(token) as jose.JWTHeaderParameters;
  const payload = jose.decodeJwt(token);

  return { header, payload, signature: parts[2] };
}

/** Formats a Unix timestamp (seconds) as a human-readable date string */
export function formatTimestamp(timestamp: number, locale?: string): string {
  const date = new Date(timestamp * 1000);
  const userLocale = locale || (typeof navigator === 'undefined' ? 'en-GB' : navigator.language) || 'en-GB';
  return date.toLocaleString(userLocale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  });
}
